import React, { useContext, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import { PrivateRoute } from './utils/PrivateRoute';
import GlobalSkeleton from './components/GlobalSkeleton';
import ToastAnnouncer from './components/ToastAnnouncer';

// Public Pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));

// Auth Pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const AdminLoginPage = React.lazy(() => import('./pages/AdminLoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));

// Dashboard Pages
const UserDashboard = React.lazy(() => import('./pages/UserDashboard'));
const HelperDashboard = React.lazy(() => import('./pages/HelperDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminTestimonials = React.lazy(() => import('./pages/AdminTestimonials'));
const TaskReviewPage = React.lazy(() => import('./pages/TaskReviewPage'));
const AdminLayout = React.lazy(() => import('./components/AdminLayout'));
const SuperAdminLayout = React.lazy(() => import('./components/SuperAdminLayout'));

function App() {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>
      <ToastAnnouncer />
      <Suspense fallback={<GlobalSkeleton />}>
        <Routes>
          {/* Public Pages */}
          <Route path="/" element={user ? (
            user.role === 'superadmin' ? (
              <Navigate to="/superadmin/overview" />
            ) : user.role === 'admin' ? (
              <Navigate to="/admin/overview" />
            ) : user.role === 'helper' ? (
              <Navigate to="/helper/dashboard" />
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : <HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Auth Routes */}
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
          <Route path="/admin/login" element={!user ? <AdminLoginPage /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
          <Route path="/reset-password/:token" element={!user ? <ResetPasswordPage /> : <Navigate to="/" />} />

          {/* User Routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute requiredRole="user">
                <UserDashboard />
              </PrivateRoute>
            }
          />

          {/* Helper Routes */}
          <Route
            path="/helper/dashboard"
            element={
              <PrivateRoute requiredRole="helper">
                <HelperDashboard />
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <PrivateRoute requiredRole="admin">
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="dashboard" element={<Navigate to="overview" replace />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="tasks/:taskId/review" element={<TaskReviewPage />} />
            <Route path=":section" element={<AdminDashboard />} />
          </Route>

          {/* SuperAdmin Routes */}
          <Route
            path="/superadmin"
            element={
              <PrivateRoute requiredRole="superadmin">
                <SuperAdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="dashboard" element={<Navigate to="overview" replace />} />
            <Route path="tasks/:taskId/review" element={<TaskReviewPage />} />

            {/* Legacy flat URLs from before the sidebar was grouped — keep old bookmarks working */}
            <Route path="users" element={<Navigate to="/superadmin/people/users" replace />} />
            <Route path="admins" element={<Navigate to="/superadmin/people/admins" replace />} />
            <Route path="helpers" element={<Navigate to="/superadmin/people/helpers" replace />} />
            <Route path="tasks" element={<Navigate to="/superadmin/verification/tasks" replace />} />
            <Route path="commissions" element={<Navigate to="/superadmin/payments/commissions" replace />} />
            <Route path="reports" element={<Navigate to="/superadmin/verification/reports" replace />} />
            <Route path="approvals" element={<Navigate to="/superadmin/verification/certificate-approvals" replace />} />
            <Route path="audit" element={<Navigate to="/superadmin/system/audit" replace />} />

            <Route path=":group" element={<SuperAdminDashboard />} />
            <Route path=":group/:sub" element={<SuperAdminDashboard />} />
          </Route>

          {/* Catch-all 404 */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
